import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ordersApi } from '../services/api';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

const STATUS_LABELS = {
  waiting_payment: 'Čeká na platbu',
  paid: 'Zaplaceno',
  packed: 'Zabaleno',
  shipped: 'Odesláno',
  pickup_ready: 'Vyzvednutí',
  cancelled: 'Zrušeno',
};
const STATUS_CLASS = {
  waiting_payment: 'badge-waiting',
  paid: 'badge-paid',
  packed: 'badge-packed',
  shipped: 'badge-shipped',
  pickup_ready: 'badge-pickup',
  cancelled: 'badge-cancelled',
};

function fmt(n) {
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(n || 0);
}

export default function Orders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const status = searchParams.get('status') || '';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1');

  const load = useCallback(() => {
    setLoading(true);
    ordersApi.list({ status, search, page, limit: 50 })
      .then(r => { setOrders(r.data); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, [status, search, page]);

  useEffect(() => { load(); }, [load]);

  function setParam(key, val) {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    if (key !== 'page') p.delete('page');
    setSearchParams(p);
  }

  const isOverdue = o => o.status === 'waiting_payment' && o.due_date && o.due_date < new Date().toISOString().slice(0, 10);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Objednávky</h1>
        <Link to="/orders/new" className="btn btn-primary">+ Nová objednávka</Link>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div className="filters">
          <input
            className="form-control" style={{ width: 220 }}
            placeholder="Hledat zákazníka, č. obj..."
            value={search}
            onChange={e => setParam('search', e.target.value)}
          />
          <select className="form-control" style={{ width: 180 }} value={status} onChange={e => setParam('status', e.target.value)}>
            <option value="">Všechny statusy</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Celkem: {total}</span>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Číslo</th>
                  <th>Zákazník</th>
                  <th>Status</th>
                  <th>Doručení</th>
                  <th>Položky</th>
                  <th style={{ textAlign: 'right' }}>Celkem</th>
                  <th>Datum</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} style={isOverdue(o) ? { background: '#fff5f5' } : {}}>
                    <td>
                      <Link to={`/orders/${o.id}`} style={{ fontWeight: 600, color: 'var(--blue)' }}>{o.order_number}</Link>
                      {o.source === 'facebook' && <span style={{ marginLeft: 4, fontSize: 11 }}>📣</span>}
                      {o.source === 'email' && <span style={{ marginLeft: 4, fontSize: 11 }}>📧</span>}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{o.customer_name}</div>
                      {o.customer_phone && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{o.customer_phone}</div>}
                    </td>
                    <td>
                      <span className={`badge ${isOverdue(o) ? 'badge-overdue' : STATUS_CLASS[o.status] || ''}`}>
                        {isOverdue(o) ? '⚠ Po splatnosti' : STATUS_LABELS[o.status] || o.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {o.delivery_method === 'post' ? '📮 Pošta' : o.delivery_method === 'pickup' ? '🏠 Vyzvednutí' : '🤝 Osobně'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}></td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(o.total_amount)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {o.created_at?.slice(0, 10)}
                    </td>
                    <td>
                      <Link to={`/orders/${o.id}`} className="btn btn-secondary btn-sm">Detail</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && <div className="empty-state">Žádné objednávky</div>}
          </div>
        )}

        {/* Pagination */}
        {total > 50 && (
          <div className="pagination">
            <button disabled={page <= 1} onClick={() => setParam('page', page - 1)}>‹</button>
            <span style={{ padding: '5px 12px', fontSize: 13 }}>Strana {page} z {Math.ceil(total / 50)}</span>
            <button disabled={page >= Math.ceil(total / 50)} onClick={() => setParam('page', page + 1)}>›</button>
          </div>
        )}
      </div>
    </div>
  );
}
